// Minimal TIFF codec for 8-bit and 16-bit RGB/RGBA images.
// Decoder supports: uncompressed (1), LZW (5), PackBits (32773).
// Encoder writes: 8-bit RGB uncompressed TIFF.

// ---------------------------------------------------------------------------
// IFD / tag constants
// ---------------------------------------------------------------------------

const TYPE_SIZES = [0, 1, 1, 2, 4, 8, 1, 1, 2, 4, 8, 4, 8];

function getUint(view: DataView, off: number, type: number, le: boolean): number {
  switch (type) {
    case 1: return view.getUint8(off);
    case 3: return view.getUint16(off, le);
    case 4: return view.getUint32(off, le);
    case 5: {
      const n = view.getUint32(off, le);
      const d = view.getUint32(off + 4, le);
      return d ? n / d : 0;
    }
    default: return view.getUint8(off);
  }
}

interface IFDEntry { values: number[] }

function parseIFD(view: DataView, ifdOff: number, le: boolean): Map<number, IFDEntry> {
  const tags = new Map<number, IFDEntry>();
  const count = view.getUint16(ifdOff, le);

  for (let i = 0; i < count; i++) {
    const eOff = ifdOff + 2 + i * 12;
    const tag   = view.getUint16(eOff, le);
    const type  = view.getUint16(eOff + 2, le);
    const cnt   = view.getUint32(eOff + 4, le);
    const sz    = TYPE_SIZES[type] ?? 1;
    const total = sz * cnt;
    const valOff = eOff + 8;

    const values: number[] = [];
    const dataOff = total <= 4 ? valOff : view.getUint32(valOff, le);
    for (let j = 0; j < cnt; j++) {
      values.push(getUint(view, dataOff + j * sz, type, le));
    }
    tags.set(tag, { values });
  }

  return tags;
}

function tag(tags: Map<number, IFDEntry>, id: number, def = 0): number {
  return tags.get(id)?.values[0] ?? def;
}

function tagArr(tags: Map<number, IFDEntry>, id: number): number[] {
  return tags.get(id)?.values ?? [];
}

// ---------------------------------------------------------------------------
// Decompressors
// ---------------------------------------------------------------------------

function unpackNone(src: Uint8Array): Uint8Array { return src; }

function unpackPackBits(src: Uint8Array, expected: number): Uint8Array {
  const out = new Uint8Array(expected);
  let si = 0, di = 0;

  while (si < src.length && di < expected) {
    // Read header byte as signed
    const hdr = src[si++]! >= 128 ? src[si - 1]! - 256 : src[si - 1]!;

    if (hdr >= 0) {
      const n = hdr + 1;
      out.set(src.subarray(si, si + n), di);
      si += n; di += n;
    } else if (hdr !== -128) {
      const n = 1 - hdr;
      out.fill(src[si++]!, di, di + n);
      di += n;
    }
  }
  return out;
}

function unpackLZW(src: Uint8Array): Uint8Array {
  const out: number[] = [];

  // MSB-first bit reader
  let buf = 0, bitsLeft = 0, pos = 0;
  const readBits = (n: number): number => {
    while (bitsLeft < n) {
      if (pos >= src.length) return -1;
      buf = (buf << 8) | src[pos++]!;
      bitsLeft += 8;
    }
    bitsLeft -= n;
    return (buf >> bitsLeft) & ((1 << n) - 1);
  };

  // String table — pre-allocate, grow lazily
  const table: Uint8Array[] = new Array(4096);
  for (let i = 0; i < 256; i++) table[i] = new Uint8Array([i]);

  let codeSize = 9, nextCode = 258, maxCode = 512;

  const reset = () => { codeSize = 9; nextCode = 258; maxCode = 512; };

  // Skip leading clear code if present
  let code = readBits(codeSize);
  if (code === 256) { reset(); code = readBits(codeSize); }
  if (code <= -1 || code === 257) return new Uint8Array(out);

  let entry = table[code]!;
  for (const b of entry) out.push(b);
  let prev = code;

  for (;;) {
    code = readBits(codeSize);
    if (code <= -1 || code === 257) break;

    if (code === 256) {
      reset();
      code = readBits(codeSize);
      if (code <= -1 || code === 257) break;
      entry = table[code]!;
      for (const b of entry) out.push(b);
      prev = code;
      continue;
    }

    let str: Uint8Array;
    if (code < nextCode) {
      str = table[code]!;
    } else {
      // code == nextCode: prev + prev[0]
      const p = table[prev]!;
      str = new Uint8Array(p.length + 1);
      str.set(p); str[p.length] = p[0]!;
    }

    for (const b of str) out.push(b);

    if (nextCode < 4096) {
      const p = table[prev]!;
      const e = new Uint8Array(p.length + 1);
      e.set(p); e[p.length] = str[0]!;
      table[nextCode++] = e;

      if (nextCode === maxCode && codeSize < 12) { codeSize++; maxCode <<= 1; }
    }

    prev = code;
  }

  return new Uint8Array(out);
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

export class TIFFDecodeError extends Error {}

export function decodeTIFF(buffer: ArrayBuffer): ImageData {
  const view = new DataView(buffer);
  const order = view.getUint16(0, false);
  if (order !== 0x4949 && order !== 0x4D4D) throw new TIFFDecodeError('Not a TIFF file');
  const le = order === 0x4949;

  const magic = view.getUint16(2, le);
  if (magic !== 42) throw new TIFFDecodeError('Invalid TIFF magic');

  const ifdOff = view.getUint32(4, le);
  const tags = parseIFD(view, ifdOff, le);

  const width        = tag(tags, 256);
  const height       = tag(tags, 257);
  const compression  = tag(tags, 259, 1);
  const photometric  = tag(tags, 262, 2);
  const spp          = tag(tags, 277, 3);          // SamplesPerPixel
  const rowsPerStrip = tag(tags, 278, height) || height;
  const bpsArr       = tagArr(tags, 258);
  const bps          = bpsArr[0] ?? 8;             // BitsPerSample (first component)
  const planar       = tag(tags, 284, 1);

  if (!width || !height) throw new TIFFDecodeError('Missing image dimensions');
  if (photometric !== 2)  throw new TIFFDecodeError(`Unsupported photometric interpretation ${photometric} (only RGB=2 supported)`);
  if (planar !== 1)       throw new TIFFDecodeError('Planar (non-chunky) TIFF not supported');
  if (compression !== 1 && compression !== 5 && compression !== 32773)
    throw new TIFFDecodeError(`Unsupported compression ${compression}`);
  if (bps !== 8 && bps !== 16)
    throw new TIFFDecodeError(`Unsupported bit depth ${bps} (only 8 or 16 supported)`);
  if (spp < 3)
    throw new TIFFDecodeError(`Unsupported SamplesPerPixel ${spp}`);

  const stripOffsets = tagArr(tags, 273);
  const stripCounts  = tagArr(tags, 279);
  const numStrips    = stripOffsets.length;

  const bytesPerRow   = width * spp * (bps === 16 ? 2 : 1);
  const stripRows     = rowsPerStrip;
  const rawOut = new Uint8Array(height * bytesPerRow);
  let outRow = 0;

  const raw = new Uint8Array(buffer);

  for (let s = 0; s < numStrips; s++) {
    const off   = stripOffsets[s]!;
    const len   = stripCounts[s]!;
    const chunk = raw.subarray(off, off + len);

    const rows  = Math.min(stripRows, height - outRow);
    const expected = rows * bytesPerRow;

    let decoded: Uint8Array;
    if (compression === 1)     decoded = unpackNone(chunk);
    else if (compression === 5)  decoded = unpackLZW(chunk);
    else                         decoded = unpackPackBits(chunk, expected);

    rawOut.set(decoded.subarray(0, expected), outRow * bytesPerRow);
    outRow += rows;
  }

  // Convert to RGBA 8-bit
  const rgba = new Uint8ClampedArray(width * height * 4);
  const scale = bps === 16 ? 1 / 257 : 1; // 65535/255 ≈ 257

  for (let i = 0; i < width * height; i++) {
    const srcOff = i * spp * (bps === 16 ? 2 : 1);
    const dstOff = i * 4;

    if (bps === 8) {
      rgba[dstOff]     = rawOut[srcOff]!;
      rgba[dstOff + 1] = rawOut[srcOff + 1]!;
      rgba[dstOff + 2] = rawOut[srcOff + 2]!;
      rgba[dstOff + 3] = spp >= 4 ? rawOut[srcOff + 3]! : 255;
    } else {
      // 16-bit big-endian in TIFF (regardless of file byte order the per-pixel bytes follow file order)
      const r = le
        ? rawOut[srcOff]! | (rawOut[srcOff + 1]! << 8)
        : (rawOut[srcOff]! << 8) | rawOut[srcOff + 1]!;
      const g = le
        ? rawOut[srcOff + 2]! | (rawOut[srcOff + 3]! << 8)
        : (rawOut[srcOff + 2]! << 8) | rawOut[srcOff + 3]!;
      const b = le
        ? rawOut[srcOff + 4]! | (rawOut[srcOff + 5]! << 8)
        : (rawOut[srcOff + 4]! << 8) | rawOut[srcOff + 5]!;
      rgba[dstOff]     = Math.round(r * scale);
      rgba[dstOff + 1] = Math.round(g * scale);
      rgba[dstOff + 2] = Math.round(b * scale);
      rgba[dstOff + 3] = spp >= 4
        ? Math.round((le
            ? rawOut[srcOff + 6]! | (rawOut[srcOff + 7]! << 8)
            : (rawOut[srcOff + 6]! << 8) | rawOut[srcOff + 7]!) * scale)
        : 255;
    }
  }

  return new ImageData(rgba, width, height);
}

// ---------------------------------------------------------------------------
// Encode — 8-bit RGB uncompressed TIFF (no alpha channel written)
// ---------------------------------------------------------------------------

export function encodeTIFF(imageData: ImageData): ArrayBuffer {
  const { width, height, data } = imageData;
  const imgBytes = width * height * 3;

  // Layout:
  //   [0..7]   : TIFF header
  //   [8..9]   : IFD entry count (9)
  //   [10..117]: 9 × 12-byte IFD entries
  //   [118..121]: next IFD offset = 0
  //   [122..127]: BitsPerSample extra data: 8,0,8,0,8,0 (3 LE SHORTs)
  //   [128..]  : image pixel data (RGB, no alpha)

  const IFD_OFF    = 8;
  const N_ENTRIES  = 9;
  const IFD_BYTES  = 2 + N_ENTRIES * 12 + 4;   // 118
  const BPS_OFF    = IFD_OFF + IFD_BYTES;        // 126
  const IMG_OFF    = BPS_OFF + 6;               // 132

  const buf = new ArrayBuffer(IMG_OFF + imgBytes);
  const view = new DataView(buf);
  const u8   = new Uint8Array(buf);

  const w16 = (off: number, v: number) => view.setUint16(off, v, true);
  const w32 = (off: number, v: number) => view.setUint32(off, v, true);

  // Header
  w16(0, 0x4949); // 'II' little-endian
  w16(2, 42);
  w32(4, IFD_OFF);

  // IFD entry count
  w16(IFD_OFF, N_ENTRIES);

  const writeEntry = (idx: number, tg: number, type: number, count: number, value: number) => {
    const off = IFD_OFF + 2 + idx * 12;
    w16(off,     tg);
    w16(off + 2, type);
    w32(off + 4, count);
    w32(off + 8, value);
  };

  // SHORT = 3, LONG = 4
  writeEntry(0, 256, 4, 1, width);                      // ImageWidth
  writeEntry(1, 257, 4, 1, height);                     // ImageLength
  writeEntry(2, 258, 3, 3, BPS_OFF);                    // BitsPerSample (offset)
  writeEntry(3, 259, 3, 1, 1);                          // Compression = none
  writeEntry(4, 262, 3, 1, 2);                          // PhotometricInterpretation = RGB
  writeEntry(5, 273, 4, 1, IMG_OFF);                    // StripOffsets
  writeEntry(6, 277, 3, 1, 3);                          // SamplesPerPixel
  writeEntry(7, 278, 4, 1, height);                     // RowsPerStrip
  writeEntry(8, 279, 4, 1, imgBytes);                   // StripByteCounts

  // Next IFD = 0
  w32(IFD_OFF + 2 + N_ENTRIES * 12, 0);

  // BitsPerSample extra data: 3 × SHORT(8)
  w16(BPS_OFF,     8);
  w16(BPS_OFF + 2, 8);
  w16(BPS_OFF + 4, 8);

  // Pixel data: strip alpha channel
  let di = IMG_OFF;
  for (let i = 0; i < data.length; i += 4) {
    u8[di++] = data[i]!;
    u8[di++] = data[i + 1]!;
    u8[di++] = data[i + 2]!;
  }

  return buf;
}
